/**
 * The change that asked "confirm it's you", kept while the page leaves for a
 * provider (`/auth/{provider}/confirm`), for Settings to take back when the
 * provider returns with the result (social sign-in PRD, Tasks 7.2 and 7.3).
 *
 * It's saved in this tab's session storage under the account
 * (`…__acct:{username}`), taken back once, and ignored after ten minutes, the
 * provider flow's own lapse, so another account, a sign-out or an old tab
 * can't resume it. It holds what to do, never a secret.
 */

import { dayjs } from '@personal-trainer-app/shared/src/utils/date';

import type { SocialProvider } from '@personal-trainer-app/shared';

/**
 * A change as data, so it can resume after a provider's round trip: what to
 * do and what it needs (a provider, a new email address), never a password.
 * Each change that can ask for a confirmation names its own `kind`.
 */
export interface ResumableChange {
  kind: string;
  params?: Record<string, string>;
}

/** What's kept while the page is away at the provider. */
export interface PendingConfirmation {
  /** The provider the page left for, so the result can name it. */
  provider: SocialProvider;
  /** The change as the modal said it: "disconnect Apple". */
  label: string;
  /** The change to resume. Absent when the change can't resume. */
  resume?: ResumableChange;
}

interface SavedConfirmation extends PendingConfirmation {
  /** When it was saved, in milliseconds since the epoch. */
  savedAt: number;
}

const STORAGE_KEY = 'confirm-identity:pending';

/** The provider flow lapses after ten minutes, and what was kept for it too. */
const LAPSE_MS = 10 * 60 * 1000;

const keyFor = (username: string): string => `${STORAGE_KEY}__acct:${username}`;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isResumableChange = (value: unknown): value is ResumableChange =>
  isRecord(value) &&
  typeof value.kind === 'string' &&
  (value.params === undefined ||
    (isRecord(value.params) &&
      Object.values(value.params).every((param) => typeof param === 'string')));

const isSavedConfirmation = (value: unknown): value is SavedConfirmation =>
  isRecord(value) &&
  (value.provider === 'google' || value.provider === 'apple') &&
  typeof value.label === 'string' &&
  typeof value.savedAt === 'number' &&
  (value.resume === undefined || isResumableChange(value.resume));

/** Keep the change for this account while the page is away at the provider. */
export function savePendingConfirmation(
  username: string,
  pending: PendingConfirmation
): void {
  const saved: SavedConfirmation = { ...pending, savedAt: dayjs().valueOf() };
  try {
    sessionStorage.setItem(keyFor(username), JSON.stringify(saved));
  } catch {
    // Storage is full or blocked: confirming still works, nothing resumes.
  }
}

/**
 * Take back this account's kept change, once: it's removed as it's read.
 * Null when there's none, when it has lapsed, or when it can't be read.
 */
export function takePendingConfirmation(
  username: string
): PendingConfirmation | null {
  const key = keyFor(username);
  let raw: string | null;
  try {
    raw = sessionStorage.getItem(key);
    sessionStorage.removeItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;

  let saved: unknown;
  try {
    saved = JSON.parse(raw);
  } catch {
    return null;
  }
  if (
    !isSavedConfirmation(saved) ||
    dayjs().valueOf() - saved.savedAt >= LAPSE_MS
  ) {
    return null;
  }

  const { provider, label, resume } = saved;
  return resume ? { provider, label, resume } : { provider, label };
}
